/**
 * AutoDebitBillingStripeCardOnFileForPlatformFeesPage — platform-fee auto-debit
 * dashboard. Shows card-on-file status (masked last4), auto-debit schedule,
 * recent-charge feed, and update-card CTA. Stripe is an integration partner
 * (card-on-file ingested from them) so the name is surfaced in help text.
 *
 * Visual: GREEN-only accent (no emerald/blue/orange/purple/dark/emoji).
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAutoDebitsList,
	useAutoDebitCreate,
} from "./hooks";
import {
	aggregateChargeStats,
	formatAmount,
	maskedLast4,
	nextDebitAt,
	type AutoDebitDto,
} from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL =
	"text-[11px] font-semibold uppercase tracking-wider text-zinc-500";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50";
const PILL_GREEN =
	"inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800";
const PILL_ZINC =
	"inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-700";
const PILL_RED =
	"inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700";

function statusPill(status: string) {
	if (status === "succeeded") return PILL_GREEN;
	if (status === "failed") return PILL_RED;
	return PILL_ZINC;
}

function formatUnix(sec: number): string {
	if (!sec) return "—";
	const d = new Date(sec * 1000);
	return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
}

/** Pick the most-recent row as the source of truth for card-on-file. */
function pickCardOnFile(rows: AutoDebitDto[]): AutoDebitDto | null {
	if (rows.length === 0) return null;
	let best = rows[0];
	for (const r of rows) if (r.updated_at > best.updated_at) best = r;
	return best;
}

export function AutoDebitBillingStripeCardOnFileForPlatformFeesPage() {
	const listQuery = useAutoDebitsList();
	const createMut = useAutoDebitCreate();

	const [amount, setAmount] = useState("");
	const [purpose, setPurpose] = useState("");

	const items = listQuery.data?.items ?? [];
	const cof = useMemo(() => pickCardOnFile(items), [items]);
	const stats = useMemo(() => aggregateChargeStats(items), [items]);
	const nextAt = useMemo(
		() => nextDebitAt(items, Math.floor(Date.now() / 1000)),
		[items],
	);

	const handleSchedule = (e: FormEvent) => {
		e.preventDefault();
		const cents = Math.round(Number(amount) * 100);
		if (!cof) return;
		if (!Number.isFinite(cents) || cents <= 0) return;
		if (!purpose.trim()) return;
		createMut.mutate(
			{
				idempotency_key: `ui-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
				stripe_customer_id: cof.stripe_customer_id,
				stripe_payment_method_id: cof.stripe_payment_method_id,
				amount_cents: cents,
				currency: cof.currency,
				purpose: purpose.trim(),
				livemode: cof.livemode,
			},
			{
				onSuccess: () => {
					setAmount("");
					setPurpose("");
				},
			},
		);
	};

	return (
		<main
			data-testid="auto-debit-billing-stripe-card-on-file-for-platform-fees-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Platform fees
				</p>
				<h1 className="bg-gradient-to-r from-green-600 to-green-800 bg-clip-text text-3xl font-semibold text-transparent">
					Auto-debit billing
				</h1>
				<p className="text-sm text-zinc-600">
					Platform fees debited automatically from your Stripe card on file.
				</p>
			</header>

			<section
				className={CARD}
				aria-label="Card on file"
				data-testid="auto-debit-billing-stripe-card-on-file-for-platform-fees-card-on-file"
			>
				<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
					<div className="flex flex-col gap-1">
						<span className={LABEL}>Card on file</span>
						{listQuery.isLoading ? (
							<span
								className="text-sm text-zinc-500"
								data-testid="auto-debit-billing-stripe-card-on-file-for-platform-fees-loading"
							>
								Loading.
							</span>
						) : cof ? (
							<div className="flex items-center gap-3">
								<span
									className="font-mono text-base font-semibold text-zinc-900"
									data-testid="auto-debit-billing-stripe-card-on-file-for-platform-fees-last4"
								>
									ending {maskedLast4(cof.stripe_payment_method_id)}
								</span>
								{cof.livemode ? (
									<span className={PILL_GREEN}>live</span>
								) : (
									<span className={PILL_ZINC}>test</span>
								)}
							</div>
						) : (
							<span
								className="text-sm text-zinc-500"
								data-testid="auto-debit-billing-stripe-card-on-file-for-platform-fees-no-card"
							>
								No card on file yet. Connect Stripe to add one.
							</span>
						)}
					</div>
					<button
						type="button"
						className={BTN_GHOST}
						data-testid="auto-debit-billing-stripe-card-on-file-for-platform-fees-update-card"
						onClick={() => {
							// Defer to platform's Stripe setup flow; noop in standalone preview.
							if (typeof window !== "undefined") {
								window.location.href = "/settings/billing/card";
							}
						}}
					>
						Update card
					</button>
				</div>
			</section>

			<section
				className={CARD}
				aria-label="Auto-debit schedule"
				data-testid="auto-debit-billing-stripe-card-on-file-for-platform-fees-schedule"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					Auto-debit schedule
				</h2>
				<div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
					<div>
						<span className={LABEL}>Next debit</span>
						<div
							className="mt-0.5 text-sm text-zinc-900"
							data-testid="auto-debit-billing-stripe-card-on-file-for-platform-fees-next-at"
						>
							{nextAt ? formatUnix(nextAt) : "—"}
						</div>
					</div>
					<div>
						<span className={LABEL}>Last succeeded</span>
						<div
							className="mt-0.5 text-sm text-zinc-900"
							data-testid="auto-debit-billing-stripe-card-on-file-for-platform-fees-succeeded-count"
						>
							{stats.succeeded}
						</div>
					</div>
					<div>
						<span className={LABEL}>Total debited</span>
						<div
							className="mt-0.5 text-sm text-zinc-900"
							data-testid="auto-debit-billing-stripe-card-on-file-for-platform-fees-total-cents"
						>
							{formatAmount(stats.succeeded_cents, cof?.currency ?? "usd")}
						</div>
					</div>
				</div>

				<form
					onSubmit={handleSchedule}
					className="mt-5 flex flex-col gap-3 sm:flex-row"
					data-testid="auto-debit-billing-stripe-card-on-file-for-platform-fees-schedule-form"
				>
					<input
						type="number"
						step="0.01"
						min="0.01"
						className={INPUT}
						placeholder="Amount"
						value={amount}
						onChange={(e) => setAmount(e.currentTarget.value)}
						disabled={createMut.isPending || !cof}
						data-testid="auto-debit-billing-stripe-card-on-file-for-platform-fees-amount-input"
					/>
					<input
						type="text"
						className={INPUT}
						placeholder="Purpose (e.g. platform_fee_apr)"
						value={purpose}
						onChange={(e) => setPurpose(e.currentTarget.value)}
						disabled={createMut.isPending || !cof}
						data-testid="auto-debit-billing-stripe-card-on-file-for-platform-fees-purpose-input"
					/>
					<button
						type="submit"
						className={BTN}
						disabled={
							createMut.isPending ||
							!cof ||
							!amount ||
							!purpose.trim()
						}
						data-testid="auto-debit-billing-stripe-card-on-file-for-platform-fees-submit"
					>
						{createMut.isPending ? "Scheduling." : "Schedule debit"}
					</button>
				</form>
				{createMut.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section
				className={CARD}
				aria-label="Recent charges"
				data-testid="auto-debit-billing-stripe-card-on-file-for-platform-fees-recent"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					Recent charges
				</h2>
				{listQuery.isLoading ? (
					<p className="mt-3 text-sm text-zinc-500">Loading.</p>
				) : listQuery.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="auto-debit-billing-stripe-card-on-file-for-platform-fees-empty"
					>
						No charges yet.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="auto-debit-billing-stripe-card-on-file-for-platform-fees-list"
					>
						{items.slice(0, 20).map((row) => (
							<li
								key={row.id}
								className="flex items-center justify-between gap-3 py-2 text-sm"
								data-testid={`auto-debit-billing-stripe-card-on-file-for-platform-fees-row-${row.id}`}
							>
								<div className="flex min-w-0 flex-col">
									<span className="truncate font-medium text-zinc-900">
										{row.purpose}
									</span>
									<span className="truncate text-xs text-zinc-500">
										{formatUnix(row.created_at)}
									</span>
								</div>
								<div className="flex items-center gap-3">
									<span className="font-mono text-xs text-zinc-700">
										{formatAmount(row.amount_cents, row.currency)}
									</span>
									<span className={statusPill(row.status)}>
										{row.status}
									</span>
								</div>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default AutoDebitBillingStripeCardOnFileForPlatformFeesPage;
